/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { api, businessPath, dateTime, money } from '../lib/api'
import { Badge, PageHeader, State } from '../components/UI'

export type Field = { key: string; label: string; type?: 'text' | 'number' | 'date' | 'select' | 'textarea'; options?: string[]; required?: boolean }
export type ResourceConfig = { title: string; description: string; endpoint: string; fields: Field[]; moneyKeys?: string[]; statusKey?: string; createLabel?: string; detailPath?: string; categoryPath?: string; categoryEndpoint?: string }
type Category = { id: string; name: string; is_active: boolean }

export default function ResourcePage({ config }: { config: ResourceConfig }) {
  const [items, setItems] = useState<Record<string, unknown>[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [categoryLoading, setCategoryLoading] = useState(Boolean(config.categoryEndpoint))
  const [error, setError] = useState('')
  const [categoryError, setCategoryError] = useState('')
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [form, setForm] = useState<Record<string, string>>({})

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      setItems(await api<Record<string, unknown>[]>(businessPath(`${config.endpoint}${query ? `?q=${encodeURIComponent(query)}` : ''}`)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load this page.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    if (!config.categoryEndpoint) return
    setCategoryLoading(true)
    setCategoryError('')
    void api<Category[]>(businessPath(config.categoryEndpoint))
      .then(setCategories)
      .catch((e) => setCategoryError(e instanceof Error ? e.message : 'Unable to load categories.'))
      .finally(() => setCategoryLoading(false))
  }, [config.categoryEndpoint])

  const columns = useMemo(() => {
    const first = items[0]
    return first
      ? Object.keys(first).filter((key) => !['id', 'business_id', 'recorded_by', 'created_by', 'updated_at'].includes(key)).slice(0, 6)
      : config.fields.slice(0, 5).map((field) => field.key)
  }, [items, config.fields])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    const body: Record<string, unknown> = {}
    config.fields.forEach((field) => {
      if (form[field.key] !== undefined && form[field.key] !== '') body[field.key] = field.type === 'number' ? Number(form[field.key]) : form[field.key]
    })
    if (config.categoryEndpoint && form.category_id) body.category_id = form.category_id
    try {
      await api(businessPath(config.endpoint), { method: 'POST', body: JSON.stringify(body) })
      setCreating(false)
      setForm({})
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save.')
    } finally {
      setSaving(false)
    }
  }

  const activeCategories = categories.filter((category) => category.is_active)

  return <section className="aad-page">
    <PageHeader title={config.title} description={config.description} action={<div className="header-actions">
      {config.categoryPath && <Link className="aad-button aad-button-secondary" to={config.categoryPath}>Manage categories</Link>}
      <button className="aad-button aad-button-primary" onClick={() => setCreating((value) => !value)}>{creating ? 'Close form' : config.createLabel ?? `Add ${config.title.slice(0, -1)}`}</button>
    </div>} />
    {error && <p className="aad-error" role="alert">{error}</p>}
    {creating && <form className="aad-card aad-card-padding resource-form" onSubmit={submit}>
      <h2 className="aad-section-title">{config.createLabel ?? `New ${config.title.slice(0, -1)}`}</h2>
      {config.categoryEndpoint && <label className="aad-field">
        <span className="aad-label">Category</span>
        <select className="aad-select" value={form.category_id ?? ''} onChange={(event) => setForm({ ...form, category_id: event.target.value })} required disabled={categoryLoading || Boolean(categoryError) || !activeCategories.length}>
          <option value="">{categoryLoading ? 'Loading categories…' : 'Select an active category…'}</option>
          {activeCategories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
        </select>
        {categoryError && <p className="aad-error" role="alert">{categoryError}</p>}
        {!categoryLoading && !categoryError && !activeCategories.length && <p className="aad-muted">No active categories. <Link to={config.categoryPath ?? '/'}>Manage categories</Link></p>}
      </label>}
      <div className="aad-form-grid">{config.fields.map((field) => <label className="aad-field" key={field.key}>
        <span className="aad-label">{field.label}</span>
        {field.type === 'textarea' ? <textarea className="aad-textarea" value={form[field.key] ?? ''} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} required={field.required} />
          : field.type === 'select' ? <select className="aad-select" value={form[field.key] ?? ''} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} required={field.required}><option value="">Select…</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</select>
            : <input className="aad-input" type={field.type ?? 'text'} value={form[field.key] ?? ''} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} required={field.required} />}
      </label>)}</div>
      <div className="form-actions"><button className="aad-button aad-button-gold" disabled={saving || Boolean(config.categoryEndpoint && (!form.category_id || categoryLoading || categoryError || !activeCategories.length))}>{saving ? 'Saving…' : 'Save'}</button></div>
    </form>}
    <div className="resource-toolbar"><input className="aad-input" placeholder={`Search ${config.title.toLowerCase()}…`} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void load() }} /><button className="aad-button aad-button-secondary" onClick={() => void load()}>Search</button></div>
    {loading ? <State title="Loading…" /> : !items.length ? <State title={`No ${config.title.toLowerCase()} yet`}>Use the button above to add your first record.</State> : <div className="aad-table-wrap"><table className="aad-table"><thead><tr>{columns.map((column) => <th key={column}>{column.replaceAll('_', ' ')}</th>)}{config.detailPath && <th>Action</th>}</tr></thead><tbody>{items.map((item) => <tr key={String(item.id)}>{columns.map((column) => <td key={column}>{column === config.statusKey || column === 'status' || column === 'is_active' ? <Badge value={item[column] === true ? 'ACTIVE' : item[column] === false ? 'INACTIVE' : item[column]} /> : config.moneyKeys?.includes(column) || /amount|total|price|fee|salary/.test(column) ? money(item[column]) : /date|created_at/.test(column) ? dateTime(item[column]) : String(item[column] ?? '—')}</td>)}{config.detailPath && <td><Link className="aad-button aad-button-secondary" to={`${config.detailPath}/${String(item.id)}`}>View</Link></td>}</tr>)}</tbody></table></div>}
  </section>
}
